import React, { useState, useEffect } from 'react';
import { BookOpen, Download, Award, Users, Briefcase, Rocket, Star, ShieldCheck, RefreshCw, ChevronDown } from 'lucide-react';
import { useLang, pickLocalized, tx } from '../i18n/LanguageContext';
import { subscriptionFeatures } from '../data/mock';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PlanCard } from '../components/PlanCard';

const iconMap = { BookOpen, Download, Award, Users, Briefcase, Rocket };

const defaultPlans = [
  {
    id: 'weekly-default', nameAr: 'الباقة الأسبوعية', nameEn: 'Weekly Plan',
    taglineAr: 'للتجربة السريعة قبل الالتزام', taglineEn: 'Try before you commit',
    icon: 'zap', price: 49, annual_price: 49 * 4 * 11, period: 'weekly', popular: false,
    features: [
      { ok: true, ar: 'وصول لجميع الكورسات', en: 'Access to all courses' },
      { ok: true, ar: 'فيديوهات تعليمية بجودة HD', en: 'HD educational videos' },
      { ok: true, ar: 'دعم فني عبر البريد', en: 'Email support' },
      { ok: false, ar: 'شهادة إنجاز معتمدة', en: 'Certified certificate' },
    ],
  },
  {
    id: 'monthly-default', nameAr: 'الباقة الشهرية', nameEn: 'Monthly Plan',
    taglineAr: 'الخيار الأفضل والأكثر شعبية', taglineEn: 'Best value & most popular',
    icon: 'crown', price: 149, annual_price: 149 * 11, period: 'monthly', popular: true,
    features: [
      { ok: true, ar: 'وصول كامل لجميع الكورسات', en: 'Full access to all courses' },
      { ok: true, ar: 'تحميل المحتوى للمشاهدة بدون نت', en: 'Download for offline viewing' },
      { ok: true, ar: 'شهادة إنجاز معتمدة', en: 'Certified completion certificates' },
      { ok: true, ar: 'دعم فني مباشر 24/7', en: 'Direct 24/7 tech support' },
    ],
  },
];

const FAQ = ({ L }) => {
  const items = [
    { q: L('هل يتجدد الاشتراك تلقائياً؟', 'Does the plan renew automatically?'), a: L('نعم، يتجدد اشتراكك تلقائياً في نهاية كل فترة حسب مدة باقتك. ويمكنك إلغاء التجديد في أي وقت من ملفك الشخصي، ويبقى اشتراكك فعالاً حتى نهاية الفترة المدفوعة.', 'Yes, your plan renews automatically at the end of each billing period. You can cancel renewal anytime from your profile and keep access until the paid period ends.') },
    { q: L('كيف يتم الدفع؟', 'How do I pay?'), a: L('الدفع آمن ببطاقتك البنكية عبر Stripe، ويُفعَّل اشتراكك فور إتمام الدفع.', 'Pay securely by card through Stripe. Your plan activates as soon as the payment completes.') },
    { q: L('هل تتوفر شهادة بعد إنهاء الكورس؟', 'Is there a certificate?'), a: L('نعم، تحصل على شهادة إنجاز معتمدة بعد إكمال الكورسات في الباقات التي تتضمنها.', 'Yes, plans that include it give you a certified completion certificate.') },
    { q: L('هل يمكن استخدام الحساب على أكثر من جهاز؟', 'Can I use multiple devices?'), a: L('نعم، سجّل الدخول من جوالك وحاسوبك وتابع التعلم من حيث توقفت.', 'Yes, log in on your phone and computer and continue where you left off.') },
  ];
  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-black text-slate-900 text-center mb-8 tracking-tight">{L('أسئلة شائعة', 'Frequently Asked Questions')}</h2>
      <div className="space-y-3">
        {items.map((item, i) => (
          <details key={i} data-testid={`faq-item-${i}`} className="group bg-white border border-slate-200 rounded-2xl px-5 sm:px-6 shadow-sm open:shadow-md transition-shadow">
            <summary className="flex items-center justify-between gap-3 py-5 text-slate-900 font-bold list-none cursor-pointer hover:text-blue-700 transition-colors text-sm sm:text-base">
              <span>{item.q}</span>
              <ChevronDown className="w-5 h-5 text-slate-400 shrink-0 transition-transform group-open:rotate-180" />
            </summary>
            <p className="text-slate-600 text-sm leading-relaxed pb-5">{item.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
};

const Subscription = () => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const [plans, setPlans] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get('/plans').then(({ data }) => setPlans(data && data.length > 0 ? data : defaultPlans)).catch(() => setPlans(defaultPlans));
  }, []);

  return (
    <main data-testid="subscription-page" className="page-y min-h-screen relative overflow-hidden bg-gradient-to-b from-blue-50/70 via-slate-50 to-slate-50 text-slate-900">
      <div className="absolute inset-x-0 top-0 h-[520px] hero-grid pointer-events-none" />
      <div className="site-container relative">
        <div className="text-center max-w-3xl mx-auto mb-10 fade-up">
          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-black text-slate-900 tracking-tight leading-tight mb-4">
            {L('اختر الباقة', 'Choose the plan')} <span className="text-blue-600">{L('المناسبة لك', 'that suits you')}</span>
          </h1>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
            {L('وصول كامل إلى جميع الدروس والاختبارات والمقالات. تعلم في أي وقت وعلى جميع الأجهزة.', 'Full access to every lesson, quiz and article. Learn anytime, on any device.')}
          </p>
        </div>

        <div dir="ltr" data-testid="plans-grid" className={`grid grid-cols-1 ${(plans || []).length >= 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2 max-w-4xl mx-auto'} gap-6 lg:gap-7 pt-4 mb-9 sm:mb-10 items-stretch`}>
          {plans ? plans.map((plan, i) => <PlanCard key={plan.id} plan={plan} user={user} i={i} active={activePlan === plan.id} onActivate={() => setActivePlan(plan.id)} />)
            : [0, 1, 2].map(i => (
              <div key={i} data-testid="plan-card-skeleton" className="bg-white rounded-3xl border border-slate-200 p-8 h-[520px] animate-pulse">
                <div className="h-14 w-14 mx-auto bg-slate-100 rounded-2xl mb-6" />
                <div className="h-10 w-1/2 mx-auto bg-slate-100 rounded-xl mb-8" />
                {[1, 2, 3, 4].map(j => <div key={j} className="h-4 bg-slate-100 rounded mb-4" />)}
              </div>
            ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-4xl mx-auto mb-9 sm:mb-10">
          {[
            { Ic: ShieldCheck, label: L('دفع آمن بالبطاقة', 'Secure card checkout') },
            { Ic: RefreshCw, label: L('ألغِ التجديد في أي وقت', 'Cancel renewal anytime') },
            { Ic: Users, label: L('+15K طالب', '+15K Students') },
            { Ic: Star, label: L('تقييم 4.9/5', '4.9/5 Rating') },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
              <item.Ic className="w-5 h-5 text-blue-600 shrink-0" />
              <span className="text-slate-700 text-xs sm:text-sm font-semibold">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="mb-9 sm:mb-10">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mb-2">{L('كل ما تحصل عليه عند الاشتراك', 'Everything you get')}</h2>
            <p className="text-slate-500 text-sm">{L('مزايا متكاملة لتعلم احترافي', 'Complete features for professional learning')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {subscriptionFeatures.map((f, i) => {
              const Ic = iconMap[f.icon] || BookOpen;
              return (
                <div key={i} className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4"><Ic className="w-5 h-5 text-blue-600" /></div>
                  <h3 className="text-slate-900 font-bold text-base sm:text-lg mb-1.5">{pickLocalized(f.title, lang)}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{pickLocalized(f.desc, lang)}</p>
                </div>
              );
            })}
          </div>
        </div>

        <FAQ L={L} />
      </div>
    </main>
  );
};

export default Subscription;
